import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";

import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import FullScreenLoader from "./Loader";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";

const OrderSummaryTable = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfr_id = selectedManufacturer?.id;

  const [orders, setOrders] = useState([]);
  const [statusOrdersObj, setStatusOrdersObj] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [activeLinks, setActiveLinks] = useState([]);
  const [selectedLink, setSelectedLink] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [loading, setLoading] = useState(true);

  const isMobile = useMediaQuery("(max-width:600px)");

  const getAllActiveLinks = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/auth/links/${mfr_id}`,
      );

      if (response.data?.statusCode !== 200) throw response.data;

      setActiveLinks(response.data.data || []);
    } catch (error) {
      console.error("Error fetching active links:", error);
    }
  };

  const getOrdersByLink = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/fetchorder/getOrders`,
      );
      setOrders(response.data.data.records || []);
      setStatusOrdersObj({
        total: response.data.data["totalRecords"] || 0,
        new: response.data.data["New_count"] || 0,
        confirmed: response.data.data["Confirm(DM)_count"] || 0,
        accepted: response.data.data["Accept(WH)_count"] || 0,
        unavailable: response.data.data["Unavailable(WH)_count"] || 0,
        rejected: response.data.data["Reject(DM)_count"] || 0,
        ordered: response.data.data["Order(DM)_count"] || 0,
        dispatched: response.data.data["Dispatched(SP)_count"] || 0,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    }
  };

  useEffect(() => {
    getOrdersByLink();
    getAllActiveLinks();
    // getOrdersByStatus();
  }, []);

  const formatDate = (isoString, type = "") => {
    if (!isoString) return "";
    const d = new Date(isoString);

    if (type === "order") {
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}-${String(d.getFullYear()).slice(-2)}, ${String(
        d.getHours(),
      ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }

    if (type === "delivery") {
      return `${String(d.getDate()).padStart(2, "0")}-${String(
        d.getMonth() + 1,
      ).padStart(2, "0")}-${String(d.getFullYear()).slice(-2)}`;
    }

    return "--";
  };

  useEffect(() => {
    const groupedMap = {};

    orders.forEach((item) => {
      const key = item.order_no;

      if (!groupedMap[key]) {
        // first occurrence
        groupedMap[key] = {
          ...item,
          pcs: item.count || 1,
          statusArray: [
            {
              tagno: item.tagno,
              status: item.status,
            },
          ],
          latestUpdatedAt: item.updatedAt, // keep for sorting old logic
        };
      } else {
        // sum pcs count
        groupedMap[key].pcs += item.count || 1;

        // push every item's tagno & status into array
        groupedMap[key].statusArray.push({
          tagno: item.tagno,
          status: item.status,
        });

        // also keep the latest updatedAt record (if needed for sorting)
        if (
          new Date(item.updatedAt) > new Date(groupedMap[key].latestUpdatedAt)
        ) {
          groupedMap[key].latestUpdatedAt = item.updatedAt;

          // optional: latest status (if still needed somewhere)
          groupedMap[key].status = item.status;
        }
      }
    });

    let temp = Object.values(groupedMap);

    // FILTERS
    if (selectedLink) {
      temp = temp.filter((o) => o.linkId === selectedLink);
    }

    if (search.trim() !== "") {
      const s = search.toLowerCase();
      temp = temp.filter(
        (o) =>
          (o.customerName || "").toString().toLowerCase().includes(s) ||
          (o.customerPhone || "").toString().toLowerCase().includes(s) ||
          (o.order_no || "").toString().toLowerCase().includes(s),
      );
    }

    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date();
      to.setHours(23, 59, 59, 999);
      temp = temp.filter((o) => {
        const orderDate = new Date(o.createdAt);
        return orderDate >= from && orderDate <= to;
      });
    }

    // SORTS
    if (sortBy === "name") {
      temp.sort((a, b) =>
        (a.customerName || "").localeCompare(b.customerName || ""),
      );
    } else if (sortBy === "mobile") {
      temp.sort((a, b) =>
        (a.customerPhone || "").localeCompare(b.customerPhone || ""),
      );
    } else if (sortBy === "date") {
      temp.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    setFilteredOrders(temp);
  }, [search, sortBy, selectedLink, fromDate, orders]);

  const exportToExcel = () => {
    const exportData = filteredOrders.map((order, idx) => ({
      S_No: idx + 1,
      Order_No: order.order_no || "--",
      Name: order.customerName || "--",
      Mobile: order.customerPhone || "--",
      LinkName:
        activeLinks.find((l) => l.linkId === order.linkId)?.name || "--",
      Pcs: order.pcs || "-",
      Order_Date: formatDate(order.createdAt, "order") || "--",
      Delivery_Date: formatDate(order.deliveryDate, "delivery") || "--",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Orders");

    const filename = `order-summary-${new Date()
      .toLocaleDateString("en-IN")
      .replace(/\//g, "-")}.xlsx`;

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      filename,
    );
  };

  const handleViewMore = (order) => {
    const mfrId =
      activeLinks.find((link) => link.linkId === order.linkId)?.mfr_id ||
      mfr_id;

    navigate("/order-summary-details", {
      state: {
        order_no: order.order_no,
        mfrId: mfrId,
        deliveryDate: order.deliveryDate
          ? new Date(order.deliveryDate)
              .toLocaleDateString("en-GB")
              .replaceAll("/", "-")
          : "-",
        customerPhone: order.customerPhone,
        linkId: order.linkId,
        customerName: order.customerName,
        customerEmail: order.customerEmail,
      },
    });
  };

  // Status Labels
  // normalized keys (all lowercased) -> label shown in UI
  const STATUS_LABELS = {
    new: "New",
    "confirm(dm)": "Confirmed",
    "accept(wh)": "Accepted",
    "reject(dm)": "Rejected",
    "unavailable(wh)": "Unavailable",
    "order(dm)": "Ordered",
    "dispatched(sp)": "Dispatched",
  };

  // desired fallback order (used when counts are equal). Keys must be normalized (lowercase)
  const STATUS_DISPLAY_ORDER = [
    "reject(dm)",
    "confirm(dm)",
    "accept(wh)",
    "new",
    "unavailable(wh)",
    "order(dm)",
    "dispatched(sp)",
  ];

  // Order Status Tiles Styles
  const statusStyles = {
    total: {
      bg: "linear-gradient(135deg, #1B2A38 0%, #2F4253 100%)",
    },
    new: {
      bg: "linear-gradient(135deg, #345B7E 0%, #4A6F8C 100%)",
    },
    confirmed: {
      bg: "linear-gradient(135deg, #2C677A 0%, #3E717D 100%)",
    },
    accepted: {
      bg: "linear-gradient(135deg, #1F5A57 0%, #2F6A63 100%)",
    },
    rejected: {
      bg: "linear-gradient(135deg, #6B1F2A 0%, #8B2F3A 100%)",
    },
    unavailable: {
      bg: "linear-gradient(135deg, #5A1A1A 0%, #7A2C2C 100%)",
    },
    ordered: {
      bg: "linear-gradient(135deg, #0A4A4D 0%, #0A5C60 100%)",
    },
    dispatched: {
      bg: "linear-gradient(135deg, #1F5A32 0%, #315F3C 100%)",
    },
  };

  // Order Status Tiles
  const tiles = [
    { key: "total", label: "Total Items", value: statusOrdersObj?.total || 0 },
    {
      key: "new",
      label: "New",
      value: statusOrdersObj?.new || 0,
      dbStatus: "New" || "new",
    },
    {
      key: "confirmed",
      label: "Confirmed",
      value: statusOrdersObj?.confirmed || 0,
      dbStatus: "Confirm(DM)",
    },
    {
      key: "accepted",
      label: "Accepted",
      value: statusOrdersObj?.accepted || 0,
      dbStatus: "Accept(WH)",
    },
    {
      key: "rejected",
      label: "Rejected",
      value: statusOrdersObj?.rejected || 0,
      dbStatus: "Reject(DM)" || "reject(DM)" || "reject",
    },
    {
      key: "unavailable",
      label: "Unavailable",
      value: statusOrdersObj?.unavailable || 0,
      dbStatus: "Unavailable(WH)",
    },
    {
      key: "ordered",
      label: "Ordered",
      value: statusOrdersObj?.ordered || 0,
      dbStatus: "Order(DM)",
    },
    {
      key: "dispatched",
      label: "Dispatched",
      value: statusOrdersObj?.dispatched || 0,
      dbStatus: "Dispatched(SP)",
    },
  ];

  // Open Order Status Table
  const openOrderStatusTable = async (orderDBStatus) => {
    let orderStatusData = [];
    try {
      const getOrdersOfSameStatusURL = `${API_BASE_URL}/api/fetchorder/getOrders?status=${orderDBStatus}`;
      const response = await axios.get(getOrdersOfSameStatusURL);
      const responseData = response.data?.data?.records;
      orderStatusData = responseData;
    } catch (error) {
      console.error(error);
    }

    navigate("/order-status", {
      state: { orderStatusData, orderStatus: orderDBStatus },
    });
  };

  return (
    <>
      <FullScreenLoader open={loading} text="Loading..." />
      <Box
        sx={{
          bgcolor: "#394A5C",
          minHeight: "100vh",
          p: isMobile ? 2 : 6,
        }}
      >
        {/* Back + Home Buttons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
            }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIosNewRoundedIcon
              sx={{ fontSize: 20, fill: "#42A996" }}
            />
          </Box>

          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
            }}
            onClick={() => navigate("/admin-dash")}
          >
            <HomeRoundedIcon sx={{ fontSize: 20, fill: "#42A996" }} />
          </Box>
        </Box>

        {/* Heading */}
        <Typography
          sx={{
            color: "#fff",
            fontSize: isMobile ? 22 : 32,
            fontWeight: 600,
            mb: 1,
          }}
        >
          ORDER SUMMARY
        </Typography>

        {/* Status Tiles */}
        <Box
          sx={{
            display: "grid",
            gap: 2,
            mb: 3,
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)", // Mobile
              sm: "repeat(4, 1fr)", // Tablet
              md: "repeat(8, 1fr)", // Desktop
            },
          }}
        >
          {tiles.map((tile) => (
            <Box
              key={tile.key}
              sx={{
                background: statusStyles[tile.key].bg,
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                color: "#FFFFFF",
                boxShadow: "0px 4px 14px rgba(0,0,0,0.25)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                cursor: tile.key !== "total" ? "pointer" : "",

                "&:hover": {
                  transform: tile.key !== "total" ? "translateY(-6px)" : "",
                  boxShadow:
                    tile.key !== "total" ? "0px 8px 20px rgba(0,0,0,0.35)" : "",
                },
              }}
              onClick={() => {
                if (tile.key !== "total") openOrderStatusTable(tile.dbStatus);
              }}
            >
              <Typography sx={{ fontSize: { xs: 14, md: 16 }, opacity: 0.85 }}>
                {tile.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 20, md: 26 },
                  fontWeight: 700,
                  mt: 0.5,
                }}
              >
                {tile.value}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Filters Row */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* SAME FILTER UI */}
          <select
            value={selectedLink}
            onChange={(e) => setSelectedLink(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "8px",
              background: "#1E2F40",
              color: "#fff",
              border: "none",
              width: isMobile ? "100%" : "220px",
            }}
          >
            <option value="">All Links</option>
            {activeLinks.map((link) => (
              <option key={link.linkId} value={link.linkId}>
                {link.name}
              </option>
            ))}
          </select>

          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Order Number, Name or Mobile"
            sx={{
              width: isMobile ? "100%" : "260px",
              background: "#1E2F40",
              borderRadius: 1,
              "& .MuiInputBase-input": {
                color: "#fff",
                padding: "10px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment sx={{ padding: 0, margin: 0 }} position="start">
                  <SearchOutlinedIcon sx={{ color: "#ffffff65", padding: 0 }} />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
          />

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "8px",
              background: "#1E2F40",
              color: "#fff",
              border: "none",
              width: isMobile ? "100%" : "200px",
            }}
          >
            <option value="date">Sort by Date</option>
            <option value="name">Sort by Name</option>
            <option value="mobile">Sort by Mobile</option>
          </select>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              padding: "10px",
              borderRadius: "8px",
              background: "#1E2F40",
              color: "#fff",
              border: "none",
              width: isMobile ? "100%" : "180px",
              appearance: "none",
              WebkitAppearance: "none",
              position: "relative",
            }}
          />

          {/* Excel Export */}
          <Box>
            <IconButton
              onClick={exportToExcel}
              sx={{
                bgcolor: "#FFF",
                borderRadius: 2,
                width: 36,
                height: 36,
                "&:hover": {
                  bgcolor: "#1c4b42ff",
                  "& svg": {
                    fill: "#ffffff", // <-- icon color on hover
                  },
                },
                "& svg": {
                  transition: "0.2s",
                  fill: "#42A996", // <-- default icon color
                },
              }}
              aria-label="Export Excel"
            >
              <ExcelExportIcon
                style={{
                  width: 18,
                  height: 18,
                }}
              />
            </IconButton>
          </Box>
        </Box>

        {/* TABLE */}
        <Box
          sx={{
            bgcolor: "#2A3A4B",
            borderRadius: 2,
            overflowX: "auto", // ✔ allow horizontal scroll
            WebkitOverflowScrolling: "touch",
            msOverflowStyle: "none", // ✔ hide scrollbar (IE/Edge)
            scrollbarWidth: "none", // ✔ hide scrollbar (Firefox)
            "&::-webkit-scrollbar": {
              display: "none", // ✔ hide scrollbar (Chrome/Safari)
            },
          }}
        >
          <Table
            sx={{
              minWidth: "900px",
              tableLayout: "auto",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Order No.
                </TableCell>

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Name
                </TableCell>

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Mobile
                </TableCell>

                {selectedLink === "" && (
                  <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                    Link Name
                  </TableCell>
                )}

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Pcs
                </TableCell>

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Order
                </TableCell>

                {/* ✅ NEW DELIVERY DATE COLUMN */}
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Delivery
                </TableCell>

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Status
                </TableCell>

                <TableCell sx={{ color: "#fff", fontWeight: 600 }}></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Show No Order Data */}
              {selectedLink && filteredOrders.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{
                      bgcolor: "#1E2F40",
                      color: "#FFF",
                      fontSize: 18,
                      py: 2,
                    }}
                  >
                    No orders found
                  </TableCell>
                </TableRow>
              )}

              {/* Show Order Data */}
              {filteredOrders.map((order, index) => (
                <TableRow
                  key={index}
                  sx={{
                    bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                  }}
                >
                  {/* Order Number */}
                  <TableCell sx={{ color: "#fff" }}>
                    {order.order_no || "-"}
                  </TableCell>

                  {/* Customer Name */}
                  <TableCell sx={{ color: "#fff" }}>
                    {order.customerName}
                  </TableCell>

                  {/* Customer Phone */}
                  <TableCell sx={{ color: "#fff" }}>
                    {order.customerPhone || ""}
                  </TableCell>

                  {/* Link Name */}
                  {selectedLink === "" && (
                    <TableCell sx={{ color: "#fff" }}>
                      {activeLinks.find((l) => l.linkId === order.linkId)
                        ?.name || ""}
                    </TableCell>
                  )}

                  {/* Pcs */}
                  <TableCell sx={{ color: "#fff" }}>
                    {order.pcs || "--"}
                  </TableCell>

                  {/* Order Placed Date */}
                  <TableCell sx={{ color: "#fff" }}>
                    {formatDate(order.createdAt, "order")}
                  </TableCell>

                  {/* Order Delivaery Date */}
                  <TableCell sx={{ color: "#fff" }}>
                    {order.deliveryDate
                      ? formatDate(order.deliveryDate, "delivery")
                      : "-"}
                  </TableCell>

                  {/* Order Status */}
                  <TableCell sx={{ color: "#fff" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {(() => {
                        if (
                          !order.statusArray ||
                          order.statusArray.length === 0
                        ) {
                          return (
                            <Typography sx={{ color: "#fff" }}>-</Typography>
                          );
                        }

                        // COUNT statuses (normalize to lowercase)
                        const counts = {};
                        order.statusArray.forEach((item) => {
                          const raw = (item.status || "").toString().trim();
                          const normalized = raw.toLowerCase();
                          if (STATUS_LABELS[normalized]) {
                            counts[normalized] = (counts[normalized] || 0) + 1;
                          }
                        });

                        const entries = Object.keys(counts).map((k) => ({
                          key: k,
                          count: counts[k],
                          label: STATUS_LABELS[k],
                        }));

                        if (entries.length === 0) {
                          return (
                            <Typography sx={{ color: "#fff" }}>-</Typography>
                          );
                        }

                        // Sort: primary by count desc, secondary by your display order
                        entries.sort((a, b) => {
                          if (b.count !== a.count) return b.count - a.count;
                          const ia = STATUS_DISPLAY_ORDER.indexOf(a.key);
                          const ib = STATUS_DISPLAY_ORDER.indexOf(b.key);
                          return (
                            (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib)
                          );
                        });

                        return entries.map((e) => (
                          <Box
                            key={e.key}
                            sx={{
                              borderRadius: 1,
                              display: "inline-flex",
                              justifyContent: "flex-start",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <Typography sx={{ color: "#fff" }}>
                              {e.label} :
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "#42A996",
                              }}
                            >
                              {e.count}
                            </Typography>
                          </Box>
                        ));
                      })()}
                    </Box>
                  </TableCell>

                  {/* View More */}
                  <TableCell>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: "#42A996",
                        "&:hover": { bgcolor: "#379e89" },
                        color: "#fff",
                        borderRadius: 2,
                        textTransform: "none",
                        px: 2,
                        width: isMobile ? "100%" : "auto",
                      }}
                      onClick={() => handleViewMore(order)}
                    >
                      View More
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </>
  );
};

export default OrderSummaryTable;
